import { z } from 'zod';
import { BagCover } from './airport';

// King's Cross, as Lagos Life's Jibowu Motor Park: a station joined to the map's north edge with a ticket office
// ("Pick a train, pay and board"). Where to: the map's own stations and airports, each served by the lines that
// really run there (the Elizabeth line, the Jubilee line, Thameslink, the DLR), in Standard or First, cheapest,
// top rated or comfiest first, with travel insurance for the bags. Then the ride: the train out of King's Cross
// through London ("Now passing: Farringdon"), watched from behind, the side or a seat, and off at the stop.
// The cities up the line (Edinburgh, York…) "open soon" until they are built. Everything here is content the
// admins edit.

const id = z.string().regex(/^[a-z0-9_]{2,30}$/);
const icon = z.string().trim().min(1).max(16);
const colour = z.string().regex(/^#[0-9a-f]{6}$/i);

// the ticket office's tabs (Lagos Life's HiAce / Coach / Sleeper): the Underground and the DLR, National Rail,
// and sleepers (players' own trains, to come)
export const RAIL_KINDS = ['metro', 'rail'] as const;
export type RailKind = (typeof RAIL_KINDS)[number];

// a line (an operator): what it is like on board, its paint, the stops it serves
export const RailLine = z.object({
  id,
  name: z.string().trim().min(2).max(30),
  kind: z.enum(RAIL_KINDS),
  // the paint on its trains: the body, the band along the windows and the doors
  colours: z.object({ body: colour, band: colour, doors: colour }),
  // how comfortable (1–3 stars) and how well rated (1–5) it is: the "Comfiest" and "Top rated" orders
  comfort: z.number().int().min(1).max(3),
  rating: z.number().min(1).max(5),
  // what you get on board, shown on the ticket and the trip's card
  perks: z.string().trim().min(1).max(80),
  // the fare compared with the stop's
  times: z.number().min(0.5).max(5),
  // First Class, where the line has it: dearer than Standard, more comfortable
  first: z.object({ times: z.number().min(1).max(10), perks: z.string().trim().min(1).max(80) }).optional(),
  // the trains' unit numbers start with this ("345" → 345 012)
  unit: z.string().regex(/^[0-9]{3}$/),
  // the stops it runs to (their places)
  serves: z.array(id).max(20),
  visible: z.boolean(),
});
export type RailLine = z.infer<typeof RailLine>;

// a stop on the map the trains run to: its place (the player gets off there), the Standard fare, how long it
// takes, how far it is and the stations passed on the way
export const RailStop = z.object({
  placeId: id,
  name: z.string().trim().min(2).max(40),
  code: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/),
  farePence: z.number().int().min(50).max(10_000_000),
  seconds: z.number().int().min(10).max(300),
  km: z.number().min(1).max(2000),
  via: z.array(z.string().trim().min(1).max(30)).max(10),
  visible: z.boolean(),
});
export type RailStop = z.infer<typeof RailStop>;

// a city up the line that opens when it is built (Lagos Life's locked cities)
export const RailCity = z.object({
  id,
  name: z.string().trim().min(2).max(40),
  icon,
  station: z.string().trim().min(2).max(40),
  line: z.string().trim().min(2).max(30),
  farePence: z.number().int().min(50).max(10_000_000),
  visible: z.boolean(),
});
export type RailCity = z.infer<typeof RailCity>;

export const RailContent = z.object({
  // the station's place, where the ticket office is and the trains leave from
  placeId: id,
  name: z.string().trim().min(2).max(40),
  code: z
    .string()
    .trim()
    .regex(/^[A-Z]{3}$/),
  platforms: z.number().int().min(1).max(30),
  // on the platform before the doors close, in real seconds
  boardingSeconds: z.number().int().min(0).max(120),
  lines: z.array(RailLine).max(12),
  stops: z.array(RailStop).max(20),
  cities: z.array(RailCity).max(12),
  // travel insurance: the bags (and a park pickpocket's chance), as the airline's bag cover
  insurance: BagCover,
  // what the trip's card says: on the platform, pulling out, along the way (`at` 0 to 1), arriving ({stop})
  script: z.object({
    boarding: z.string().trim().min(1).max(120),
    leaving: z.string().trim().min(1).max(120),
    lines: z.array(z.object({ at: z.number().min(0).max(1), text: z.string().trim().min(1).max(120) })).max(12),
    arriving: z.string().trim().min(1).max(120),
  }),
});
export type RailContent = z.infer<typeof RailContent>;

export const DEFAULT_RAIL: RailContent = {
  placeId: 'kingscross',
  name: "King's Cross",
  code: 'KGX',
  platforms: 11,
  boardingSeconds: 8,
  lines: [
    {
      id: 'elizabeth',
      name: 'Elizabeth line',
      kind: 'rail',
      colours: { body: '#f4f5f7', band: '#6950a1', doors: '#6950a1' },
      comfort: 2,
      rating: 4.6,
      perks: 'Air-con, walk-through carriages, free Wi-Fi',
      times: 1,
      unit: '345',
      serves: ['tube', 'excel', 'heathrow'],
      visible: true,
    },
    {
      id: 'thameslink',
      name: 'Thameslink',
      kind: 'rail',
      colours: { body: '#f2f3f5', band: '#d0367f', doors: '#2b2f3a' },
      comfort: 2,
      rating: 4.1,
      perks: 'Air-con, plug sockets, room for bags',
      times: 1,
      first: { times: 1.6, perks: 'First Class at the front: wider seats, a table, quiet' },
      unit: '700',
      serves: ['gatwick'],
      visible: true,
    },
    {
      id: 'jubilee',
      name: 'Jubilee line',
      kind: 'metro',
      colours: { body: '#d9dde1', band: '#868f98', doors: '#c81e1e' },
      comfort: 1,
      rating: 4.3,
      perks: 'A train every two minutes, mind the gap',
      times: 0.9,
      unit: '996',
      serves: ['tube', 'northgreenwich'],
      visible: true,
    },
    {
      id: 'dlr',
      name: 'DLR',
      kind: 'metro',
      colours: { body: '#eef1f3', band: '#00a4a7', doors: '#00a4a7' },
      comfort: 1,
      rating: 4.4,
      perks: 'No driver: grab the front seat for the view',
      times: 0.95,
      unit: '230',
      serves: ['airport', 'excel'],
      visible: true,
    },
  ],
  stops: [
    {
      placeId: 'tube',
      name: 'Canary Wharf',
      code: 'CWF',
      farePence: 340,
      seconds: 24,
      km: 9,
      via: ['Farringdon', 'Liverpool Street', 'Whitechapel'],
      visible: true,
    },
    {
      placeId: 'northgreenwich',
      name: 'North Greenwich',
      code: 'NGW',
      farePence: 340,
      seconds: 26,
      km: 11,
      via: ['Farringdon', 'London Bridge', 'Canada Water', 'Canary Wharf'],
      visible: true,
    },
    {
      placeId: 'excel',
      name: 'Custom House for ExCeL',
      code: 'CUS',
      farePence: 360,
      seconds: 28,
      km: 13,
      via: ['Farringdon', 'Liverpool Street', 'Whitechapel', 'Canary Wharf'],
      visible: true,
    },
    {
      placeId: 'airport',
      name: 'London City Airport',
      code: 'LCY',
      farePence: 380,
      seconds: 30,
      km: 15,
      via: ['Moorgate', 'Bank', 'Shadwell', 'Canning Town'],
      visible: true,
    },
    {
      placeId: 'heathrow',
      name: 'Heathrow Airport',
      code: 'LHR',
      farePence: 1300,
      seconds: 40,
      km: 27,
      via: ['Farringdon', 'Paddington', 'Ealing Broadway', 'Hayes & Harlington'],
      visible: true,
    },
    {
      placeId: 'gatwick',
      name: 'Gatwick Airport',
      code: 'LGW',
      farePence: 2050,
      seconds: 45,
      km: 46,
      via: ['Farringdon', 'Blackfriars', 'London Bridge', 'East Croydon'],
      visible: true,
    },
  ],
  cities: [
    {
      id: 'edinburgh',
      name: 'Edinburgh',
      icon: '🏰',
      station: 'Waverley',
      line: 'LNER',
      farePence: 9000,
      visible: true,
    },
    { id: 'york', name: 'York', icon: '🚂', station: 'York', line: 'LNER', farePence: 6500, visible: true },
    {
      id: 'newcastle',
      name: 'Newcastle',
      icon: '🌉',
      station: 'Central',
      line: 'LNER',
      farePence: 7500,
      visible: true,
    },
    { id: 'leeds', name: 'Leeds', icon: '🦉', station: 'Leeds', line: 'LNER', farePence: 6000, visible: true },
    {
      id: 'cambridge',
      name: 'Cambridge',
      icon: '🎓',
      station: 'Cambridge',
      line: 'Great Northern',
      farePence: 2600,
      visible: true,
    },
  ],
  insurance: { pence: 150, lossPercent: 10, lostEffects: { fun: -18 } },
  script: {
    boarding: '🚉 On the platform: find a seat, the doors close in a moment',
    leaving: "🚆 Out of King's Cross, past the gasholders",
    lines: [
      { at: 0.25, text: '📱 Phones out, headphones in' },
      { at: 0.5, text: '☕ The trolley comes through: tea, coffee, crisps' },
      { at: 0.75, text: '🎒 People reach for their bags' },
    ],
    arriving: '🛬 Arriving at {stop}: mind the gap',
  },
};

// content made before the station uses the default
export const railOf = (content: { rail?: RailContent }) => content.rail ?? DEFAULT_RAIL;

// a ticket: a line to a stop, Standard or First
export type RailService = {
  line: RailLine;
  first: boolean;
  farePence: number;
  comfort: number;
  perks: string;
};
// the trains to a stop, as the ticket office lists them (every visible line serving it, Standard and First)
export function servicesTo(rail: RailContent, stop: RailStop): RailService[] {
  return rail.lines
    .filter((line) => line.visible && line.serves.includes(stop.placeId))
    .flatMap((line) => [
      {
        line,
        first: false,
        farePence: Math.round(stop.farePence * line.times),
        comfort: line.comfort,
        perks: line.perks,
      },
      ...(line.first
        ? [
            {
              line,
              first: true,
              farePence: Math.round(stop.farePence * line.times * line.first.times),
              comfort: Math.min(3, line.comfort + 1),
              perks: line.first.perks,
            },
          ]
        : []),
    ]);
}
export const RAIL_ORDERS = ['cheapest', 'rated', 'comfiest'] as const;
export type RailOrder = (typeof RAIL_ORDERS)[number];
export function sortServices(services: RailService[], order: RailOrder) {
  const by: Record<RailOrder, (a: RailService, b: RailService) => number> = {
    cheapest: (a, b) => a.farePence - b.farePence,
    rated: (a, b) => b.line.rating - a.line.rating || a.farePence - b.farePence,
    comfiest: (a, b) => b.comfort - a.comfort || a.farePence - b.farePence,
  };
  return [...services].sort(by[order]);
}
// the stops the trains run to now: visible, with at least one line running there
export const railStopsOf = (rail: RailContent) =>
  rail.stops.filter((stop) => stop.visible && servicesTo(rail, stop).length > 0);

export function railProblems(rail: RailContent): string[] {
  const problems: string[] = [];
  const lines = new Set<string>();
  for (const line of rail.lines) {
    if (lines.has(line.id)) problems.push(`Two lines have the id "${line.id}".`);
    lines.add(line.id);
  }
  const stops = new Set<string>();
  for (const stop of rail.stops) {
    if (stops.has(stop.placeId)) problems.push(`Two train stops are the place "${stop.placeId}".`);
    if (stop.placeId === rail.placeId) problems.push(`${stop.name}: a train cannot go to the station it leaves.`);
    stops.add(stop.placeId);
  }
  for (const line of rail.lines)
    for (const placeId of line.serves)
      if (!stops.has(placeId)) problems.push(`${line.name} runs to "${placeId}", which is not a stop.`);
  const cities = new Set<string>();
  for (const city of rail.cities) {
    if (cities.has(city.id)) problems.push(`Two cities up the line have the id "${city.id}".`);
    cities.add(city.id);
  }
  return problems;
}

// a train trip: to a stop, on a line, Standard or First, insured or not (the station is where it leaves from)
export const TrainRequest = z.object({
  to: id,
  // a line's train (its id), or a player's company's (with `company` and the kind of train it runs there)
  line: id,
  first: z.boolean(),
  covered: z.boolean(),
  company: z.string().uuid().optional(),
});
export type TrainRequest = z.infer<typeof TrainRequest>;
